// src/editor/EditablePage.jsx
'use client';
import Popup from '@/components/Popup';
import {
    extractInlineFontFamilies,
    getSiteFontRequestWeights,
} from '@/utils/google-fonts';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css';
import { EditableComponent } from './EditableComponent';
import GlobalEditor from './GlobalEditor';
import { GoogleFontLoader } from './GoogleFontLoader';
import { PageCustomCodeInjector } from './PageCustomCodeInjector';
import {
    getEditorPreviewNavigation,
    scrollPreviewToHash,
} from './helpers/previewNavigation';
import {
    EditorContextProvider,
    useEditor,
} from './context/EditorContext/EditorContext';
import { NotificationContextProvider } from './context/NotificationContext/NotificationContext';

// Helper hook
function useViewportHeight() {
    const [vpHeight, setVpHeight] = useState(0);
    useEffect(() => {
        if (typeof window === 'undefined') return;
        function handleResize() {
            setVpHeight(window.innerHeight);
        }
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    // 48px = navbar
    return vpHeight - 48;
}

export function EditablePage(props) {
    const {
        page_name,
        company_data,
        page_data,
        components,
        dormantPopupProps,
    } = props;

    const [viewMode, setViewMode] = useState('desktop');
    const [activeBreakpoint, setActiveBreakpoint] = useState('desktop');
    const [numericWidth, setNumericWidth] = useState(768);

    const handleBreakpointClick = useCallback((breakpoint) => {
        setActiveBreakpoint(breakpoint);
        if (breakpoint === 'desktop') {
            setViewMode('desktop');
        } else {
            setViewMode('resizable');
            setNumericWidth(breakpoint === 'tablet' ? 768 : 420);
        }
    }, []);

    return (
        <NotificationContextProvider>
            <EditorContextProvider
                page_name={page_name}
                global_data={company_data}
                page_data={page_data}
                components={components}
            >
                <GlobalEditor
                    activeBreakpoint={activeBreakpoint}
                    onBreakpointClick={handleBreakpointClick}
                    onAutoBreakpointChange={setActiveBreakpoint}
                />
                <div className="flex min-h-[calc(100vh-48px)] w-full">
                    <PreviewFrame
                        viewMode={viewMode}
                        numericWidth={numericWidth}
                        setNumericWidth={setNumericWidth}
                        setActiveBreakpoint={setActiveBreakpoint}
                    >
                        <EditablePageWithContext
                            dormantPopupProps={dormantPopupProps}
                        />
                    </PreviewFrame>
                    <div id="editor-drawer" className="shrink-0" />
                </div>
            </EditorContextProvider>
        </NotificationContextProvider>
    );
}

// PreviewFrame
function PreviewFrame({
    viewMode,
    numericWidth,
    setNumericWidth,
    setActiveBreakpoint,
    children,
}) {
    const editor = useEditor();
    const router = useRouter();
    const [iframeRef, setIframeRef] = useState(null);
    const [iframeDocument, setIframeDocument] = useState(null);
    const [iframeBody, setIframeBody] = useState(null);
    const [iframeStylesReady, setIframeStylesReady] = useState(false);
    const [iframeContentReady, setIframeContentReady] = useState(false);
    const [loadedFontRequest, setLoadedFontRequest] = useState({
        document: null,
        key: '',
    });
    const [isResizing, setIsResizing] = useState(false);
    const iFrameHeight = useViewportHeight();

    const previewFonts = useMemo(() => {
        const entries = [
            {
                family: editor.site.data?.fonts?.display,
                selectedWeight: editor.site.data?.fontWeights?.display,
                fallbackWeights: [400, 700, 800],
            },
            {
                family: editor.site.data?.fonts?.text,
                selectedWeight: editor.site.data?.fontWeights?.text,
                fallbackWeights: [400, 600, 700],
            },
            {
                family: editor.global.data?.logoTextFont,
                selectedWeight: editor.global.data?.logoTextFontWeight,
                fallbackWeights: [400, 600, 700],
            },
            ...extractInlineFontFamilies([
                editor.components.data,
                editor.page.data,
            ]).map((family) => ({
                family,
                fallbackWeights: [400, 700],
            })),
        ];
        const byFamily = new Map();

        entries.forEach(({ family, selectedWeight, fallbackWeights }) => {
            const font = String(family || '')
                .replace(/["'`<>]/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
            if (!font) return;

            const key = font.toLowerCase();
            const weights = getSiteFontRequestWeights({
                family: font,
                selectedWeight,
                fallbackWeights,
            });
            const current = byFamily.get(key);
            byFamily.set(key, {
                font,
                weights: [
                    ...new Set([...(current?.weights || []), ...weights]),
                ],
            });
        });

        return Array.from(byFamily.values());
    }, [
        editor.global.data?.logoTextFont,
        editor.global.data?.logoTextFontWeight,
        editor.components.data,
        editor.page.data,
        editor.site.data?.fontWeights?.display,
        editor.site.data?.fontWeights?.text,
        editor.site.data?.fonts?.display,
        editor.site.data?.fonts?.text,
    ]);
    const uploadedFonts = useMemo(
        () =>
            (Array.isArray(editor.global.data?.uploadedFonts)
                ? editor.global.data.uploadedFonts
                : []
            ).filter((font) => font?.family && font?.url),
        [editor.global.data?.uploadedFonts]
    );
    const previewFontRequestKey = useMemo(
        () => JSON.stringify({ fonts: previewFonts, uploadedFonts }),
        [previewFonts, uploadedFonts]
    );
    const handlePreviewFontsReady = useCallback(() => {
        if (!iframeDocument) return;
        setLoadedFontRequest({
            document: iframeDocument,
            key: previewFontRequestKey,
        });
    }, [iframeDocument, previewFontRequestKey]);
    const iframeFontsReady =
        loadedFontRequest.document === iframeDocument &&
        loadedFontRequest.key === previewFontRequestKey;

    // The preview is rendered into an about:blank iframe via a React portal.
    // If a normal internal anchor is allowed to navigate that iframe, the
    // destination page renders there and the portal is mounted beside it,
    // duplicating site chrome such as the navigation. Route through the host
    // app instead so the destination is loaded as a fresh editable page.
    useEffect(() => {
        if (!iframeDocument) return undefined;

        const handlePreviewClick = (event) => {
            if (
                event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
            ) {
                return;
            }

            const eventElement =
                event.target?.nodeType === 1
                    ? event.target
                    : event.target?.parentElement;
            if (eventElement?.closest?.('[data-inline-editable-text="true"]')) {
                return;
            }
            const anchor = eventElement?.closest?.('a[href]');
            if (
                !anchor ||
                anchor.hasAttribute('download') ||
                String(anchor.getAttribute('target') || '').toLowerCase() ===
                    '_blank'
            ) {
                return;
            }

            const navigation = getEditorPreviewNavigation(
                anchor.getAttribute('href'),
                window.location.href
            );
            if (!navigation) return;

            event.preventDefault();
            event.stopPropagation();

            if (navigation.isSameDocument) {
                if (navigation.hash) {
                    scrollPreviewToHash(iframeDocument, navigation.hash);
                } else {
                    iframeDocument.defaultView?.scrollTo?.({
                        top: 0,
                        behavior: 'smooth',
                    });
                }
                return;
            }

            if (
                editor.has_updates &&
                !window.confirm(
                    'You have unsaved changes. Switch page without saving?'
                )
            ) {
                return;
            }

            router.push(navigation.href);
        };

        iframeDocument.addEventListener('click', handlePreviewClick, true);
        return () => {
            iframeDocument.removeEventListener(
                'click',
                handlePreviewClick,
                true
            );
        };
    }, [editor.has_updates, iframeDocument, router]);

    useEffect(() => {
        if (!iframeRef) {
            setIframeDocument(null);
            setIframeBody(null);
            return undefined;
        }

        let cancelled = false;
        let frameId = 0;

        const syncIframeNodes = () => {
            if (cancelled) return;

            const doc = iframeRef.contentDocument;
            const body = doc?.body || null;

            if (doc) {
                setIframeDocument((prev) => (prev === doc ? prev : doc));
            }

            if (body) {
                setIframeBody((prev) => (prev === body ? prev : body));
                return;
            }

            frameId = window.requestAnimationFrame(syncIframeNodes);
        };

        syncIframeNodes();
        iframeRef.addEventListener('load', syncIframeNodes);

        return () => {
            cancelled = true;
            iframeRef.removeEventListener('load', syncIframeNodes);
            window.cancelAnimationFrame(frameId);
        };
    }, [iframeRef]);

    // Mirror host stylesheets into iframe and keep them synced.
    // Font Awesome kit styles are injected asynchronously, so one-time copying
    // can miss them and leave icons blank until a full refresh.
    useEffect(() => {
        if (!iframeDocument) return;
        const doc = iframeDocument;
        if (!doc) return;
        const STYLE_SELECTOR = 'link[rel="stylesheet"], style';
        const MIRROR_ATTR = 'data-editor-mirrored-style';
        const sourceToClone = new Map();
        let rafId = null;
        let cancelled = false;
        let revealGeneration = 0;

        setIframeStylesReady(false);
        setIframeContentReady(false);

        const syncDocumentRoot = () => {
            const sourceRoot = document.documentElement;
            const targetRoot = doc.documentElement;
            if (!sourceRoot || !targetRoot) return;

            targetRoot.className = sourceRoot.className;
            targetRoot.lang = sourceRoot.lang || 'en';

            // Preserve runtime CSS variables like selected fonts/colors that
            // are applied inline on the host <html> element.
            Array.from(targetRoot.style).forEach((propertyName) => {
                if (
                    propertyName === 'width' ||
                    propertyName === 'min-width' ||
                    propertyName === 'height'
                ) {
                    return;
                }
                targetRoot.style.removeProperty(propertyName);
            });

            Array.from(sourceRoot.style).forEach((propertyName) => {
                targetRoot.style.setProperty(
                    propertyName,
                    sourceRoot.style.getPropertyValue(propertyName),
                    sourceRoot.style.getPropertyPriority(propertyName)
                );
            });
        };

        const waitForStylesheets = async (generation) => {
            const mirroredLinks = Array.from(
                doc.head.querySelectorAll(
                    `link[rel="stylesheet"][${MIRROR_ATTR}="true"]`
                )
            );

            await Promise.all(
                mirroredLinks.map(
                    (linkNode) =>
                        new Promise((resolve) => {
                            let timeoutId = null;

                            const done = () => {
                                if (timeoutId !== null) {
                                    window.clearTimeout(timeoutId);
                                }
                                linkNode.removeEventListener('load', done);
                                linkNode.removeEventListener('error', done);
                                resolve();
                            };

                            if (linkNode.sheet) {
                                resolve();
                                return;
                            }

                            linkNode.addEventListener('load', done, {
                                once: true,
                            });
                            linkNode.addEventListener('error', done, {
                                once: true,
                            });

                            timeoutId = window.setTimeout(done, 1500);
                            window.requestAnimationFrame(() => {
                                if (linkNode.sheet) done();
                            });
                        })
                )
            );

            if (doc.fonts?.ready) {
                try {
                    await Promise.race([
                        doc.fonts.ready,
                        new Promise((resolve) =>
                            window.setTimeout(resolve, 1500)
                        ),
                    ]);
                } catch {
                    // Ignore font readiness failures and still reveal.
                }
            }

            if (cancelled || generation !== revealGeneration) return;

            setIframeStylesReady(true);
        };

        const mirrorStyles = () => {
            syncDocumentRoot();

            const sourceStyles = Array.from(
                document.querySelectorAll(STYLE_SELECTOR)
            ).filter(
                (sourceNode) =>
                    !sourceNode.hasAttribute('data-editor-font-loader')
            );
            const activeSources = new Set(sourceStyles);

            for (const [sourceNode, cloneNode] of sourceToClone.entries()) {
                if (!activeSources.has(sourceNode) || !sourceNode.isConnected) {
                    cloneNode.remove();
                    sourceToClone.delete(sourceNode);
                }
            }

            sourceStyles.forEach((sourceNode) => {
                let cloneNode = sourceToClone.get(sourceNode);
                if (!cloneNode) {
                    cloneNode = sourceNode.cloneNode(true);
                    cloneNode.setAttribute(MIRROR_ATTR, 'true');
                    sourceToClone.set(sourceNode, cloneNode);
                } else if (sourceNode.tagName === 'STYLE') {
                    const nextText = sourceNode.textContent || '';
                    if (cloneNode.textContent !== nextText) {
                        cloneNode.textContent = nextText;
                    }
                } else {
                    ['href', 'rel', 'type', 'media', 'crossorigin'].forEach(
                        (attr) => {
                            const nextValue = sourceNode.getAttribute(attr);
                            if (nextValue === null) {
                                cloneNode.removeAttribute(attr);
                            } else {
                                cloneNode.setAttribute(attr, nextValue);
                            }
                        }
                    );
                    cloneNode.disabled = sourceNode.disabled;
                }

                // Keep same relative order as source styles.
                doc.head.appendChild(cloneNode);
            });

            doc.documentElement.style.width = '100%';
            doc.documentElement.style.minWidth = '100%';
            doc.documentElement.style.height = '100%';
            doc.documentElement.style.overflowX = 'clip';
            doc.body.style.width = '100%';
            doc.body.style.minWidth = '100%';
            doc.body.style.height = '100%';
            doc.body.style.margin = '0';
            doc.body.style.overflowX = 'clip';
            // Inject preview-only overrides for desktop mode to avoid
            // site-level max-width utilities constraining the iframe.
            try {
                let overrideStyle = doc.getElementById(
                    'editor-preview-overrides'
                );
                const css = `
                    /* Editor preview overrides */
                    /* Preserve component max-width and centering while allowing the preview frame to fill the iframe. */
                    .w-[min(92vw,72rem)], .w-[min(92vw,32rem)] {
                        width: 100% !important;
                        max-width: 100% !important;
                    }
                `;

                if (!overrideStyle) {
                    overrideStyle = doc.createElement('style');
                    overrideStyle.id = 'editor-preview-overrides';
                    doc.head.appendChild(overrideStyle);
                }
                overrideStyle.textContent = css;
            } catch (err) {
                // Ignore failures when iframe is not fully ready.
            }
        };

        const scheduleMirror = () => {
            if (rafId !== null) return;
            rafId = window.requestAnimationFrame(() => {
                rafId = null;
                mirrorStyles();
            });
        };

        // Reset only previously mirrored nodes, preserving iframe-owned tags.
        doc.head.querySelectorAll(`[${MIRROR_ATTR}="true"]`).forEach((node) => {
            node.remove();
        });

        mirrorStyles();
        revealGeneration += 1;
        waitForStylesheets(revealGeneration);

        const observer = new MutationObserver(() => {
            scheduleMirror();
        });

        observer.observe(document.head, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
            attributeFilter: [
                'href',
                'rel',
                'type',
                'media',
                'crossorigin',
                'disabled',
            ],
        });

        const rootObserver = new MutationObserver(() => {
            syncDocumentRoot();
        });

        rootObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class', 'lang', 'style'],
        });

        return () => {
            cancelled = true;
            observer.disconnect();
            rootObserver.disconnect();
            if (rafId !== null) {
                window.cancelAnimationFrame(rafId);
            }
            for (const cloneNode of sourceToClone.values()) {
                cloneNode.remove();
            }
            sourceToClone.clear();
        };
    }, [iframeDocument]);

    useEffect(() => {
        if (!iframeStylesReady || !iframeFontsReady || !iframeBody) {
            setIframeContentReady(false);
            return undefined;
        }

        let firstFrame = 0;
        let secondFrame = 0;

        firstFrame = window.requestAnimationFrame(() => {
            secondFrame = window.requestAnimationFrame(() => {
                setIframeContentReady(true);
            });
        });

        return () => {
            window.cancelAnimationFrame(firstFrame);
            window.cancelAnimationFrame(secondFrame);
        };
    }, [iframeBody, iframeFontsReady, iframeStylesReady]);

    const iframeComponent = (
        <iframe
            ref={setIframeRef}
            title="Page Preview"
            style={{
                display: 'block',
                width: '100%',
                minWidth: 0,
                height: '100%',
                border: 'none',
                backgroundColor: '#FFF',
                visibility:
                    iframeContentReady && iframeFontsReady
                        ? 'visible'
                        : 'hidden',
                boxSizing: 'border-box',
            }}
        >
            {iframeBody &&
                createPortal(
                    <>
                        <GoogleFontLoader
                            extraFonts={previewFonts}
                            uploadedFonts={uploadedFonts}
                            targetDocument={iframeDocument}
                            includeCatalog={false}
                            onReady={handlePreviewFontsReady}
                        />
                        {iframeStylesReady && iframeFontsReady
                            ? children
                            : null}
                    </>,
                    iframeBody
                )}
        </iframe>
    );

    return (
        <div
            className="w-full min-w-0 flex-1 bg-gradient-to-br from-neutral-700 to-neutral-800"
            style={{
                display: 'flex',
                justifyContent: 'stretch',
                alignItems: 'stretch',
                width: '100%',
                maxWidth: '100%',
                minHeight: 'calc(100vh - 48px)',
                overflow: 'auto',
            }}
        >
            {viewMode === 'desktop' ? (
                // Full-width desktop
                <div
                    style={{
                        width: '100%',
                        minWidth: 0,
                        height: '100%',
                        display: 'flex',
                    }}
                >
                    {iframeComponent}
                </div>
            ) : (
                // Resizable preview
                <ResizableBox
                    className="preview-resizable"
                    width={numericWidth}
                    height={iFrameHeight}
                    axis="x"
                    resizeHandles={['w', 'e']}
                    onResizeStart={() => setIsResizing(true)}
                    onResizeStop={(e, data) => {
                        setIsResizing(false);
                        const newWidth = data.size.width;
                        setNumericWidth(newWidth);

                        if (newWidth <= 500) {
                            setActiveBreakpoint('mobile');
                        } else if (newWidth <= 900) {
                            setActiveBreakpoint('tablet');
                        } else {
                            setActiveBreakpoint('desktop');
                        }
                    }}
                    minConstraints={[320, 200]}
                    maxConstraints={[1800, iFrameHeight]}
                    style={{
                        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                    }}
                >
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            position: 'relative',
                        }}
                    >
                        {isResizing && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    zIndex: 10,
                                    userSelect: 'none',
                                }}
                            />
                        )}
                        {iframeComponent}
                    </div>
                </ResizableBox>
            )}
        </div>
    );
}

// EditablePageWithContext
function EditablePageWithContext({ dormantPopupProps }) {
    const editor = useEditor();
    const navBehaviorValue = editor.components.data.find(
        (component) => component?.name === 'Nav'
    )?.props?.Behavior;
    const rawNavBehavior =
        navBehaviorValue &&
        typeof navBehaviorValue === 'object' &&
        'value' in navBehaviorValue
            ? navBehaviorValue.value
            : navBehaviorValue;
    const showAfterHero = [
        'show-after-hero',
        'hidden-until-after-hero',
    ].includes(
        String(rawNavBehavior || '')
            .toLowerCase()
            .trim()
            .replace(/[_\s]+/g, '-')
    );
    const navColorSchemeValue = editor.components.data.find(
        (component) => component?.name === 'Nav'
    )?.props?.ColorScheme;
    const rawNavColorScheme =
        navColorSchemeValue &&
        typeof navColorSchemeValue === 'object' &&
        'value' in navColorSchemeValue
            ? navColorSchemeValue.value
            : navColorSchemeValue;
    const transparentNav = String(rawNavColorScheme || '')
        .toLowerCase()
        .trim()
        .startsWith('transparent');
    const insideHeadTag = String(editor.page.data?.InsideHeadTag || '').trim();
    const beforeBodyTag = String(editor.page.data?.BeforeBodyTag || '').trim();
    const hasPopup = editor.components.data.some(
        (component) => component?.name === 'Popup'
    );
    const hasFloatingAction = editor.components.data.some(
        (component) => component?.name === 'FloatingAction'
    );
    const shouldRenderDormantPopup =
        !hasPopup && hasFloatingAction && dormantPopupProps;

    return (
        <div
            className={`w-full ${
                showAfterHero || transparentNav
                    ? 'nav-overlay-first-content'
                    : ''
            }`}
        >
            {insideHeadTag && (
                <PageCustomCodeInjector
                    headHtml={insideHeadTag}
                    pageName={editor.page.name}
                />
            )}

            {editor.components.data.map((component, index) => (
                <EditableComponent key={component.data_file} index={index} />
            ))}

            {shouldRenderDormantPopup && (
                <Popup {...dormantPopupProps} Enabled={false} is_edit_mode />
            )}

            {beforeBodyTag && (
                <div
                    data-page-custom-before-body={editor.page.name}
                    dangerouslySetInnerHTML={{ __html: beforeBodyTag }}
                />
            )}
        </div>
    );
}
